import { Injectable } from '@angular/core';

import { map, merge, Observable, tap } from 'rxjs';

import { Location } from '@shared/location/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { UserApiService } from '../../../../frontend-auth/src/lib/user-api.service';
import { AuthLocationQuery, AuthLocationStore } from '../states';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
export class AuthLocationService {
  constructor(
    private readonly store: AuthLocationStore,
    private readonly query: AuthLocationQuery,
    private readonly userApiService: UserApiService,
    private readonly socketService: SocketService,
  ) { }

  load(): Observable<Location[]> {
    return this.userApiService.getLocations()
      .pipe(tap(locations => this.store.set(locations)));
  }

  selectAll(): Observable<Location[]> {
    return this.query.selectAll();
  }

  selectByAccountId(accountId: string): Observable<Location[]> {
    return this.query.selectAll({
      filterBy: [entity => entity.accountId === accountId],
    });
  }

  selectAllEnabled(): Observable<Location[]> {
    return this.query.selectAll().pipe(map(locations => locations.filter(({enabled}) => enabled)));
  }

  getAll(): Location[] {
    return this.query.getAll();
  }

  getById(id: string): Location {
    return this.query.getEntity(id);
  }

  listenToChanges() {
    return merge(
      this.socketService
        .getEventStream<Location | Location[]>(WebSocketEventsEnum.locationCreated)
        // TODO: add filtering?
        .pipe(tap(location => this.store.add(location))),
      this.socketService
        .getEventStream<Location>(WebSocketEventsEnum.locationUpdated)
        // TODO: update only if location is in store?
        .pipe(tap(location => this.store.update(location._id, location))),
      this.socketService
        .getEventStream<string[]>(WebSocketEventsEnum.locationDeleted)
        .pipe(
          tap(ids => {
            // The next line most likely unnecessary since we send several events as one
            // this.tokenService.removeByLocationIds(ids);
            this.store.remove(ids);
          }),
        ),
    );
  }

  reset() {
    return this.store.reset();
  }
}
